import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaVideo, FaCopy, FaExternalLinkAlt } from 'react-icons/fa';
import { buildPatientLink } from '../../../Services/videoConsultation';
import exportToExcel from '../../../UtilityHookFunctions/exportToExcel';

const STATUS_STYLES = {
  Scheduled: 'bg-sky-100 text-sky-700',
  Live: 'bg-emerald-100 text-emerald-700',
  Ended: 'bg-slate-100 text-slate-600',
  Cancelled: 'bg-rose-100 text-rose-700',
};

const fmtWhen = (session) => {
  if (!session?.bookDate) return '—';
  const slot = session.bookTimeSlot;
  return `${session.bookDate}${slot?.startTime ? ` · ${slot.startTime}` : ''}`;
};

export default function ConsultationsTab({ consultations = [] }) {
  const copy = async (token) => {
    try {
      await navigator.clipboard.writeText(buildPatientLink(token));
      toast.success('Patient join link copied');
    } catch (error) {
      toast.error('Could not copy the link');
    }
  };

  if (!consultations.length) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center">
        <FaVideo className="mx-auto h-8 w-8 text-slate-300" />
        <p className="mt-3 font-medium text-slate-700">No video consultations yet</p>
        <p className="mt-1 text-sm text-slate-500">
          A room is created automatically when an online appointment is booked for this patient.
        </p>
        <Link
          to="/video-consultation"
          className="mt-4 inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Go to video consultations
        </Link>
      </div>
    );
  }

  const handleExport = () =>
    exportToExcel(consultations, 'Video_Consultations', [
      { key: 'bookDate', label: 'When', format: (_, c) => fmtWhen(c) },
      { key: 'doctor', label: 'Doctor', format: (d) => d?.name || d?.doctorName || '' },
      { key: 'status', label: 'Status' },
      {
        key: 'startedAt',
        label: 'Duration (min)',
        format: (_, c) =>
          c.startedAt && c.endedAt
            ? Math.max(1, Math.round((new Date(c.endedAt) - new Date(c.startedAt)) / 60000))
            : '',
      },
      { key: 'guestToken', label: 'Patient Link', format: (t) => (t ? buildPatientLink(t) : '') },
    ]);

  return (
    <div className="space-y-2">
    <div className="flex justify-end">
      <button
        type="button"
        onClick={handleExport}
        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
      >
        Export to Excel
      </button>
    </div>
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-2.5 font-semibold">When</th>
            <th className="px-4 py-2.5 font-semibold">Doctor</th>
            <th className="px-4 py-2.5 font-semibold">Status</th>
            <th className="px-4 py-2.5 font-semibold">Duration</th>
            <th className="px-4 py-2.5" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {consultations.map((c) => {
            const minutes =
              c.startedAt && c.endedAt
                ? Math.max(1, Math.round((new Date(c.endedAt) - new Date(c.startedAt)) / 60000))
                : null;

            return (
              <tr key={c._id} className="hover:bg-slate-50">
                <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-800">{fmtWhen(c)}</td>
                <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                  {c.doctor?.name || c.doctor?.doctorName || '—'}
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      STATUS_STYLES[c.status] || 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {c.status}
                  </span>
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-slate-600">{minutes ? `${minutes} min` : '—'}</td>
                <td className="whitespace-nowrap px-4 py-3 text-right">
                  {c.guestToken && (
                    <button
                      onClick={() => copy(c.guestToken)}
                      className="inline-flex items-center gap-1.5 rounded p-1.5 text-slate-500 hover:bg-slate-100"
                      title="Copy the patient's join link"
                    >
                      <FaCopy />
                    </button>
                  )}
                  {c.appointmentId && (
                    <Link
                      to={`/video-consultation/${c.appointmentId}`}
                      className="inline-flex items-center gap-1.5 rounded p-1.5 text-primary-600 hover:bg-primary-50"
                      title="Open the consultation"
                    >
                      <FaExternalLinkAlt />
                    </Link>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </div>
  );
}
